import GeoJsonFetchLayer from './GeoJsonFetchLayer';
import { centroidOfGeometry } from '../utils/coords';
import { addOrUpdateGeoJsonSource, registerCursorHandlers, removeSourceAndLayers } from '../utils/maplibre-helpers';

export const BUILDING_BASE_STYLE = {
    color: '#9f9588',
    weight: 1,
    fillColor: '#e7e1d7',
    fillOpacity: 0.7,
    interactive: true
};

export const BUILDING_SELECTED_STYLE = {
    color: '#c2410c',
    weight: 3,
    fillColor: 'rgba(241,90,36,0.2)',
    fillOpacity: 0.7
};

export const BUILDING_FLOOR_HEIGHT_METERS = 3;
const BUILDING_SELECTED_3D_COLOR = '#f15a24';
const BUILDING_SELECTED_3D_OPACITY = 0.7;

const BUILDING_ADDRESS_LABEL_STYLE = {
    layout: {
        'text-field': ['get', 'numer_adresowy'],
        'text-size': 11,
        'text-allow-overlap': false,
        'text-ignore-placement': false
    },
    paint: {
        'text-color': '#5f3b24',
        'text-halo-color': 'rgba(255,255,255,0.95)',
        'text-halo-width': 1.5
    }
};

function buildingHeightExpression() {
    return [
        '*',
        BUILDING_FLOOR_HEIGHT_METERS,
        ['min', 80, ['max', 1, ['to-number', ['get', 'kondygnacje'], 1]]]
    ];
}

function getFeatureCollectionCenter(data) {
    if (!data || !Array.isArray(data.features) || !data.features.length) {
        return null;
    }

    try {
        const bounds = new maplibregl.LngLatBounds();
        let hasBounds = false;

        data.features.forEach((feature) => {
            if (!feature || !feature.geometry) {
                return;
            }

            const center = centroidOfGeometry(feature.geometry);
            if (!center) {
                return;
            }

            bounds.extend(center);
            hasBounds = true;
        });

        return hasBounds ? {
            lat: bounds.getCenter().lat,
            lng: bounds.getCenter().lng
        } : null;
    } catch (_error) {
        return null;
    }
}

export default class BudynkiLayer extends GeoJsonFetchLayer {
    static ID = 'budynki';
    static DESCRIPTION = 'Budynki z Ewidencji Gruntów i Budynków + adresy';
    static MIN_ZOOM = 15;
    static SOURCE_ID = 'src-budynki';
    static BASE_LAYER_IDS = ['lyr-budynki-fill', 'lyr-budynki-line', 'lyr-budynki-address-label'];
    static SELECTED_SOURCE_ID = 'src-selected-budynek';
    static SELECTED_LAYER_IDS = ['lyr-selected-budynek-fill', 'lyr-selected-budynek-line'];
    static RENDER_ORDER_LAYER_IDS = BudynkiLayer.BASE_LAYER_IDS.concat(BudynkiLayer.SELECTED_LAYER_IDS);

    constructor(config = {}) {
        super(config);
        this.interactionsBound = false;
    }

    getSourceId() {
        return BudynkiLayer.SOURCE_ID;
    }

    getLayerIds() {
        return BudynkiLayer.BASE_LAYER_IDS;
    }

    getRenderOrderLayerIds() {
        return BudynkiLayer.RENDER_ORDER_LAYER_IDS;
    }

    buildUrl() {
        return 'api/budynki?bbox=' + this.config.getCurrentBbox()
            + '&zoom=' + this.config.getCurrentZoom();
    }

    ensureInteractionHandlers() {
        const map = this.getMap();
        if (!map || this.interactionsBound) {
            return;
        }

        BudynkiLayer.BASE_LAYER_IDS.forEach((layerId) => {
            if (map.getLayer(layerId)) {
                registerCursorHandlers(map, layerId);
            }
        });

        this.interactionsBound = true;
    }

    getFeatureKey(feature) {
        const props = feature && feature.properties ? feature.properties : {};
        if (props.id != null && props.id !== '') {
            return props.id;
        }
        if (props.id_budynku != null && props.id_budynku !== '') {
            return props.id_budynku;
        }
        return this.featureGeometryKey(feature);
    }

    _addBaseLayers(map, sourceId) {
        map.addLayer({
            id: BudynkiLayer.BASE_LAYER_IDS[0],
            type: 'fill-extrusion',
            source: sourceId,
            paint: {
                'fill-extrusion-color': BUILDING_BASE_STYLE.fillColor,
                'fill-extrusion-base': 0,
                'fill-extrusion-height': buildingHeightExpression(),
                'fill-extrusion-opacity': BUILDING_BASE_STYLE.fillOpacity
            }
        });
        map.addLayer({
            id: BudynkiLayer.BASE_LAYER_IDS[1],
            type: 'line',
            source: sourceId,
            paint: {
                'line-color': BUILDING_BASE_STYLE.color,
                'line-width': BUILDING_BASE_STYLE.weight
            }
        });
        map.addLayer({
            id: BudynkiLayer.BASE_LAYER_IDS[2],
            type: 'symbol',
            source: sourceId,
            minzoom: 17,
            layout: BUILDING_ADDRESS_LABEL_STYLE.layout,
            paint: BUILDING_ADDRESS_LABEL_STYLE.paint
        });
        this.ensureInteractionHandlers();
    }

    renderData(data) {
        const map = this.getMap();
        if (!map) {
            return null;
        }

        return this.renderSourceData(data, this._addBaseLayers.bind(this));
    }

    clearSelectedFeature() {
        const map = this.getMap();
        if (!map) {
            return;
        }

        removeSourceAndLayers(map, BudynkiLayer.SELECTED_SOURCE_ID, BudynkiLayer.SELECTED_LAYER_IDS);
    }

    renderSelectedFeature(data) {
        const map = this.getMap();
        if (!map || !data || !Array.isArray(data.features) || !data.features.length) {
            this.clearSelectedFeature();
            return null;
        }

        this.clearSelectedFeature();

        addOrUpdateGeoJsonSource(map, BudynkiLayer.SELECTED_SOURCE_ID, data);
        map.addLayer({
            id: BudynkiLayer.SELECTED_LAYER_IDS[0],
            type: 'fill-extrusion',
            source: BudynkiLayer.SELECTED_SOURCE_ID,
            paint: {
                'fill-extrusion-color': BUILDING_SELECTED_3D_COLOR,
                'fill-extrusion-base': 0,
                'fill-extrusion-height': buildingHeightExpression(),
                'fill-extrusion-opacity': BUILDING_SELECTED_3D_OPACITY
            }
        });
        map.addLayer({
            id: BudynkiLayer.SELECTED_LAYER_IDS[1],
            type: 'line',
            source: BudynkiLayer.SELECTED_SOURCE_ID,
            paint: {
                'line-color': BUILDING_SELECTED_STYLE.color,
                'line-width': BUILDING_SELECTED_STYLE.weight
            }
        });

        return {
            layer: BudynkiLayer.SELECTED_SOURCE_ID,
            center: getFeatureCollectionCenter(data)
        };
    }
}
